import { useApolloClient } from '@apollo/client';
import { Combobox, Icon } from '@zillow/constellation-10';
import { Fragment, useContext } from 'react';
import { ConversationList_GetConversationsDocument, ReadRepliedStatus } from '../../../../types/GraphqlTypes';
import useInboxRouterHelper from '../../../../hooks/useInboxRouterHelper';
import { MAX_OLD_CONVERSATION_DAYS } from '../../../../constants/ConfigConstants';
import usePropertyFilter, { type UpdatePathWithListingAlias } from '../../../../hooks/usePropertyFilter';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { Skeleton, SkeletonListItem } from './Skeleton';
import { IsVisible } from '@zg-rentals/react-use';
import { IconSearchFilled } from '@zillow/constellation-icons';

export default function PropertySearchBar() {
  const apolloClient = useApolloClient();
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const { clientTimeZone, daysBack, folder, listingAlias, isByProperty, readRepliedStatus, renterProgressStatus } =
    useContext(InboxPageContext);
  const filteredListingAlias = isByProperty && listingAlias ? listingAlias : undefined;

  const resetPropertyFilter = async () => {
    if (readRepliedStatus !== ReadRepliedStatus.ShowAll) {
      await apolloClient.query({
        query: ConversationList_GetConversationsDocument,
        variables: {
          listingAlias: null,
          clientTimeZone,
          daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
          folder,
          renterProgressStatus,
          readRepliedStatus,
        },
        fetchPolicy: 'network-only',
      });
    }

    await changeFiltersOrFolders({
      folder,
      readRepliedStatus,
      renterProgressStatus,
      byProperty: false,
      listingAlias: undefined,
    });
  };

  const updatePathWithListingAlias: UpdatePathWithListingAlias = async (listingAlias) => {
    await changeFiltersOrFolders({
      folder,
      readRepliedStatus,
      renterProgressStatus,
      byProperty: true,
      listingAlias,
    });
  };

  const {
    defaultListingAddress,
    errorMessage,
    handleChange,
    handleScroll,
    handleSelectProperty,
    hasMoreToPaginate,
    isLoadingSearchResults,
    propertyListings,
    shouldShowSkeleton,
  } = usePropertyFilter({
    updatePathWithListingAlias,
    resetPropertyFilter,
    defaultListingAliasOnLoad: filteredListingAlias,
  });

  if (!errorMessage && shouldShowSkeleton) {
    return <Skeleton />;
  }

  return (
    <Combobox
      appearance="select"
      fluidDropdown
      focusFirstOption
      freeForm
      loading={isLoadingSearchResults}
      onChange={handleChange}
      onOptionSelect={handleSelectProperty}
      options={propertyListings}
      placeholder="Search by property address"
      selectCallbackFrequency="change"
      shouldAwaitInteractionResponse
      showLabelForValue
      size="md"
      value={defaultListingAddress || filteredListingAlias}
      renderAdornment={({ ref, ...rest }) => (
        <Combobox.Adornment ref={typeof ref === 'string' ? undefined : ref} {...rest}>
          <Icon render={<IconSearchFilled />} />
        </Combobox.Adornment>
      )}
      renderOption={({
        option,
        highlightPattern,
        key,
        ref,
        renderOptionLabel,
        renderOptionMeta,
        renderOptionMedia: _renderOptionMedia,
        ...rest
      }) => {
        if (typeof option !== 'object') return;

        const { value, label, meta } = option;
        const isLastItemInList = value === propertyListings[propertyListings.length - 1].value;

        return (
          <Fragment key={key}>
            <Combobox.Option option={option} ref={typeof ref === 'string' ? undefined : ref} {...rest}>
              {renderOptionLabel?.({ children: label, highlightPattern })}
              {meta && renderOptionMeta?.({ children: meta, highlightPattern })}
            </Combobox.Option>

            {isLastItemInList && hasMoreToPaginate && (
              <IsVisible onChange={handleScroll} options={{ rootMargin: '0px 0px 200px 0px' }}>
                {({ ref }) => <SkeletonListItem ref={ref} />}
              </IsVisible>
            )}
          </Fragment>
        );
      }}
    />
  );
}
